class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => {
      if (evt.code === "Escape") this.close();
    });

    this.handleOverlayClick = this.close.bind(this);
    this.countdownInterval = null;

    const overlay = this.querySelector("#CartDrawer-Overlay");

    if (overlay) {
      overlay.addEventListener(
        "click",
        this.handleOverlayClick
      );
    }

    this.setHeaderCartIconAccessibility();
    this.bindUpsellButton();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector(
      "#cart-icon-bubble"
    );

    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  bindUpsellButton() {
    this.addEventListener("click", (event) => {
      const upsellButton = event.target.closest(
        ".upsell-add-button"
      );

      if (!upsellButton) return;

      event.preventDefault();

      const form = upsellButton.closest(
        ".upsell-form"
      );

      if (!form) return;

      const variantInput = form.querySelector(
        'input[name="id"]'
      );

      if (!variantInput) return;

      this.addItemToCart(variantInput.value);
    });
  }

  async addItemToCart(variantId) {
    const sections = this.getSectionsToRender().map(
      (section) => section.id
    );

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(
        `${routes.cart_add_url}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body,
        }
      );

      if (response.status === 422) {
        const errorData = await response.json();

        console.error(
          "Error adding item to cart:",
          errorData.description
        );

        return;
      }

      if (!response.ok) {
        console.error(
          "Unable to add item to cart:",
          response.status
        );

        return;
      }

      const parsedState = await response.json();

      this.renderContents(parsedState);
    } catch (error) {
      console.error(
        "Error adding item to cart:",
        error
      );
    }
  }

  /**
   * Refresh cart drawer after AJAX cart operations.
   * Can be called manually through window.refreshCartDrawer().
   *
   * IMPORTANT:
   * Quantity +/- updates are already handled by CartItems in cart.js.
   * This file does not listen for cart:ajax-update because that created
   * a second refresh and caused the countdown timer to blink.
   */
  async refreshCartDrawer(sectionsData = null) {
    if (sectionsData && sectionsData.sections) {
      const dataToRender = {
        ...sectionsData,

        id:
          sectionsData.id ||
          this.productId ||
          null,

        item_count:
          typeof sectionsData.item_count === "number"
            ? sectionsData.item_count
            : null,
      };

      this.renderContents(dataToRender);

      return;
    }

    try {
      const sections = this.getSectionsToRender().map(
        (section) => section.id
      );

      const url =
        `${window.location.pathname}` +
        `?sections=${sections.join(",")}`;

      const response = await fetch(url);

      if (!response.ok) {
        console.error(
          "Unable to refresh cart drawer:",
          response.status
        );

        return;
      }

      const parsedState = await response.json();

      let returnedSections = null;

      if (parsedState.sections) {
        returnedSections = parsedState.sections;
      } else {
        returnedSections = {};

        const sectionIds = this
          .getSectionsToRender()
          .map((section) => section.id);

        sectionIds.forEach((sectionId) => {
          if (parsedState[sectionId]) {
            returnedSections[sectionId] =
              parsedState[sectionId];
          }
        });
      }

      if (
        !returnedSections ||
        Object.keys(returnedSections).length === 0
      ) {
        console.error(
          "No sections found in response:",
          parsedState
        );

        return;
      }

      const dataToRender = {
        sections: returnedSections,

        id:
          parsedState.id ||
          this.productId ||
          null,

        item_count:
          typeof parsedState.item_count === "number"
            ? parsedState.item_count
            : null,
      };

      this.renderContents(dataToRender);
    } catch (error) {
      console.error(
        "Error refreshing cart drawer:",
        error
      );
    }
  }

  startCountdownTimer() {
    const timerElement = document.getElementById(
      "cart-countdown-timer"
    );

    if (!timerElement) return;

    /*
     * Do not start a second interval if the timer
     * is already running.
     */
    if (this.countdownInterval) return;

    let minutes = 10;
    let seconds = 0;

    const updateTimer = () => {
      timerElement.textContent =
        `${minutes
          .toString()
          .padStart(2, "0")}:` +
        `${seconds
          .toString()
          .padStart(2, "0")}`;
    };

    updateTimer();

    this.countdownInterval = setInterval(() => {
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(
            this.countdownInterval
          );

          this.countdownInterval = null;

          return;
        }

        minutes -= 1;
        seconds = 59;
      } else {
        seconds -= 1;
      }

      updateTimer();
    }, 1000);
  }

  open(triggeredBy) {
    const wasAlreadyOpen =
      this.classList.contains("active");

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute("role")
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }

    /*
     * Only run the opening animation when
     * the drawer is actually closed.
     */
    if (!wasAlreadyOpen) {
      setTimeout(() => {
        this.classList.add(
          "animate",
          "active"
        );
      });

      this.addEventListener(
        "transitionend",
        () => {
          const containerToTrapFocusOn =
            this.classList.contains("is-empty")
              ? this.querySelector(
                  ".drawer__inner-empty"
                )
              : document.getElementById(
                  "CartDrawer"
                );

          const focusElement =
            this.querySelector(
              ".drawer__inner"
            ) ||
            this.querySelector(
              ".drawer__close"
            );

          if (
            containerToTrapFocusOn &&
            focusElement &&
            typeof trapFocus === "function"
          ) {
            trapFocus(
              containerToTrapFocusOn,
              focusElement
            );
          }
        },
        {
          once: true,
        }
      );
    }

    document.body.classList.add(
      "overflow-hidden"
    );

    this.initializeUpsellSlider();

    /*
     * Quantity updates must not restart
     * the countdown.
     */
    if (!this.countdownInterval) {
      this.startCountdownTimer();
    }

    /*
     * Only fire the open event for a real opening,
     * not a quantity refresh.
     */
    if (!wasAlreadyOpen) {
      this.dispatchEvent(
        new CustomEvent(
          "cart-drawer:open",
          {
            bubbles: true,
            detail: {
              triggeredBy,
            },
          }
        )
      );
    }
  }

  close() {
    const cartDrawer =
      document.querySelector(
        "cart-drawer"
      );

    const cartCustomizationMode =
      cartDrawer &&
      cartDrawer.classList.contains(
        "customization-mode"
      );

    if (cartCustomizationMode) {
      alert(
        "Cart Customization Mode is active. " +
        "Disable it in Theme Settings > Cart Drawer " +
        "to allow closing."
      );

      return;
    }

    this.classList.remove("active");

    if (
      typeof removeTrapFocus === "function"
    ) {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove(
      "overflow-hidden"
    );

    if (this.countdownInterval) {
      clearInterval(
        this.countdownInterval
      );

      this.countdownInterval = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute(
      "role",
      "button"
    );

    cartDrawerNote.setAttribute(
      "aria-expanded",
      "false"
    );

    const nextElement =
      cartDrawerNote.nextElementSibling;

    if (
      nextElement &&
      nextElement.getAttribute("id")
    ) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        nextElement.id
      );
    }

    cartDrawerNote.addEventListener(
      "click",
      (event) => {
        const details =
          event.currentTarget.closest(
            "details"
          );

        if (!details) return;

        event.currentTarget.setAttribute(
          "aria-expanded",
          !details.hasAttribute("open")
        );
      }
    );

    if (
      cartDrawerNote.parentElement &&
      typeof onKeyUpEscape === "function"
    ) {
      cartDrawerNote.parentElement.addEventListener(
        "keyup",
        onKeyUpEscape
      );
    }
  }

  renderContents(parsedState) {
    const wasAlreadyOpen =
      this.classList.contains("active");

    const sections =
      parsedState?.sections || {};

    this.productId =
      parsedState?.id ||
      this.productId;

    const cartDrawerHTML =
      sections["cart-drawer"] ||
      sections.CartDrawer ||
      null;

    const currentCartDrawer =
      document.getElementById(
        "CartDrawer"
      );

    if (
      cartDrawerHTML &&
      currentCartDrawer
    ) {
      const newCartDrawer =
        this.getSectionDOM(
          cartDrawerHTML,
          "#CartDrawer"
        );

      if (newCartDrawer) {
        const currentCartItems =
          currentCartDrawer.querySelector(
            "cart-drawer-items"
          );

        const newCartItems =
          newCartDrawer.querySelector(
            "cart-drawer-items"
          );

        const currentFooter =
          currentCartDrawer.querySelector(
            ".drawer__footer"
          );

        const newFooter =
          newCartDrawer.querySelector(
            ".drawer__footer"
          );

        const newDrawerWrapper =
          newCartDrawer.closest(
            "cart-drawer"
          );

        const itemCountIsKnown =
          typeof parsedState?.item_count ===
          "number";

        const newCartIsEmpty =
          (
            itemCountIsKnown &&
            parsedState.item_count === 0
          ) ||
          newDrawerWrapper?.classList.contains(
            "is-empty"
          ) ||
          newCartItems?.classList.contains(
            "is-empty"
          ) ||
          !newCartItems;

        const canUpdateWithoutReplacingTimer =
          wasAlreadyOpen &&
          !newCartIsEmpty &&
          currentCartItems &&
          newCartItems;

        if (
          canUpdateWithoutReplacingTimer
        ) {
          /*
           * Update only the parts that change.
           *
           * Do not replace:
           * - #CartDrawer
           * - #CartDrawer-TimerBar
           * - #cart-countdown-timer
           */
          currentCartItems.innerHTML =
            newCartItems.innerHTML;

          currentCartItems.className =
            newCartItems.className;

          if (
            currentFooter &&
            newFooter
          ) {
            currentFooter.innerHTML =
              newFooter.innerHTML;
          }

          this.classList.remove(
            "is-empty"
          );
        } else if (
          typeof updateCartDrawer ===
          "function"
        ) {
          /*
           * A full replacement is used only when:
           * - Opening from an empty cart
           * - Removing the final item
           */
          updateCartDrawer(
            currentCartDrawer,
            newCartDrawer
          );
        } else {
          currentCartDrawer.innerHTML =
            newCartDrawer.innerHTML;
        }
      }
    }

    /*
     * Update the cart icon and item quantity.
     */
    const cartIconHTML =
      sections["cart-icon-bubble"];

    const cartIconElement =
      document.getElementById(
        "cart-icon-bubble"
      );

    if (
      cartIconHTML &&
      cartIconElement
    ) {
      const iconType =
        cartIconElement.getAttribute(
          "data-cart-icon-type"
        );

      const newIconHTML =
        this.getSectionInnerHTML(
          cartIconHTML
        );

      if (
        typeof newIconHTML === "string"
      ) {
        cartIconElement.innerHTML =
          newIconHTML;
      }

      if (iconType) {
        cartIconElement.setAttribute(
          "data-cart-icon-type",
          iconType
        );
      }

      const iconWrapper =
        cartIconElement.querySelector(
          ".svg-wrapper"
        );

      if (
        iconWrapper &&
        iconType
      ) {
        const bagIcon =
          iconWrapper.querySelector(
            ".cart-icon-bag"
          );

        const cartIcon =
          iconWrapper.querySelector(
            ".cart-icon-cart"
          );

        const cartIconEmpty =
          iconWrapper.querySelector(
            ".cart-icon-cart-empty"
          );

        if (iconType === "bag") {
          if (bagIcon) {
            bagIcon.style.display =
              "flex";
          }
        } else {
          const countIsKnown =
            typeof parsedState?.item_count ===
            "number";

          const cartEmpty =
            countIsKnown
              ? parsedState.item_count === 0
              : !cartIconElement.querySelector(
                  ".cart-count-bubble"
                );

          if (
            cartEmpty &&
            cartIconEmpty
          ) {
            cartIconEmpty.style.display =
              "flex";
          } else if (cartIcon) {
            cartIcon.style.display =
              "flex";
          }
        }
      }
    }

    setTimeout(() => {
      const overlay = this.querySelector(
        "#CartDrawer-Overlay"
      );

      if (overlay) {
        overlay.removeEventListener(
          "click",
          this.handleOverlayClick
        );

        overlay.addEventListener(
          "click",
          this.handleOverlayClick
        );
      }

      /*
       * Do not trigger cart-drawer:render
       * and do not call open() when the
       * drawer is already open.
       *
       * Those actions can trigger another
       * refresh or animation.
       */
      if (!wasAlreadyOpen) {
        this.dispatchEvent(
          new CustomEvent(
            "cart-drawer:render",
            {
              bubbles: true,
              detail: {
                parsedState,
              },
            }
          )
        );

        this.open();
      }
    });

    this.initializeUpsellSlider();
  }

  initializeUpsellSlider() {
    if (
      typeof $ === "undefined" ||
      typeof $.fn === "undefined" ||
      typeof $.fn.slick === "undefined"
    ) {
      return;
    }

    const $slider = $(
      ".gbfrequently-bought-with-main-whole-slider"
    );

    if (
      $slider.length > 0 &&
      !$slider.hasClass(
        "slick-initialized"
      )
    ) {
      $slider.slick({
        infinite: true,
        autoplaySpeed: 1000,
        autoplay: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        dots: true,
      });

      $slider.css(
        "opacity",
        "1"
      );
    }
  }

  getSectionInnerHTML(
    html,
    selector = ".shopify-section"
  ) {
    if (!html) return "";

    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        "text/html"
      );

    const selectedElement =
      parsedDocument.querySelector(
        selector
      );

    return selectedElement
      ? selectedElement.innerHTML
      : "";
  }

  getSectionsToRender() {
    setTimeout(() => {
      this.initializeUpsellSlider();
    }, 500);

    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(
    html,
    selector = ".shopify-section"
  ) {
    if (!html) return null;

    return new DOMParser()
      .parseFromString(
        html,
        "text/html"
      )
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (
  !customElements.get(
    "cart-drawer"
  )
) {
  customElements.define(
    "cart-drawer",
    CartDrawer
  );
}

function setupAjaxCartHandler() {
  /*
   * FINAL TIMER-BLINK FIX
   *
   * Do not listen for cart:ajax-update here.
   *
   * The CartItems class in cart.js already
   * handles quantity increase and decrease.
   *
   * Listening to cart:ajax-update here caused
   * a second cart-drawer refresh.
   */

  window.refreshCartDrawer =
    async function (
      sectionsData = null
    ) {
      const drawer =
        document.querySelector(
          "cart-drawer"
        );

      if (drawer) {
        await drawer.refreshCartDrawer(
          sectionsData
        );
      }
    };
}

if (
  document.readyState === "loading"
) {
  document.addEventListener(
    "DOMContentLoaded",
    setupAjaxCartHandler
  );
} else {
  setupAjaxCartHandler();
}

/*
 * Wait for CartItems to be defined before
 * extending it.
 */
if (
  typeof CartItems !== "undefined"
) {
  defineCartDrawerItems();
} else {
  const checkCartItems =
    setInterval(() => {
      if (
        typeof CartItems !==
        "undefined"
      ) {
        clearInterval(
          checkCartItems
        );

        defineCartDrawerItems();
      }
    }, 50);
}

function defineCartDrawerItems() {
  if (
    customElements.get(
      "cart-drawer-items"
    )
  ) {
    return;
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      setTimeout(() => {
        const drawer =
          document.querySelector(
            "cart-drawer"
          );

        if (
          drawer &&
          typeof drawer
            .initializeUpsellSlider ===
            "function"
        ) {
          drawer.initializeUpsellSlider();
        }
      }, 500);

      /*
       * Quantity changes update only:
       *
       * 1. Cart items
       * 2. Cart footer
       * 3. Header cart icon
       *
       * The timer/header section is not included.
       */
      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector:
            "cart-drawer-items",
        },
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector:
            ".drawer__footer",
        },
        {
          id: "cart-icon-bubble",
          section:
            "cart-icon-bubble",
          selector:
            ".shopify-section",
        },
      ];
    }
  }

  customElements.define(
    "cart-drawer-items",
    CartDrawerItems
  );
}